import { MINIMUM_TILE_IDS } from '@pams/shared';
import { DASHBOARD_TILE_LABEL } from '@/lib/labels';

/**
 * This page's own words. Tile names are not here — they are `DASHBOARD_TILE_LABEL`. [P6b]
 */
export const LAYOUT_COPY = {
  title: 'Dashboard tiles',
  subtitle: 'What each role sees when they open PA Admin, per level of the organisation.',
  role: 'Role',
  level: 'Level',
  neverEdited: 'Seeded default — nobody has arranged this one yet.',
  arrangedBy: (name: string, when: string): string => `Last changed by ${name} on ${when}.`,
  borrowed: 'This combination has no arrangement of its own and is showing another level’s.',
  onLayout: 'On this dashboard',
  onLayoutHint: 'Drag to reorder, or use the ⋯ menu. The order here is the order they appear in.',
  whatThisCounts: 'What this counts',
  /** §3b/§3c — said out loud, beside the fields, every time. */
  /**
   * ⚠️ [amended C1e §3] REPLACES `hintIsStructural`, WHICH SAID "it cannot be changed here"
   * AND IS NOW FALSE. The caveat is overridable; what has not changed is that it is where the
   * number states its own boundary, so the screen has to say that rather than stop saying
   * anything.
   */
  hintIsPartOfTheNumber:
    'This sentence is where the number states its own boundary. Changing it does not change the number.',
  hintOverride: 'Description on this dashboard',
  /**
   * ⚠️ [C1e §3b] THE TWO SENTENCES SIT SIDE BY SIDE BECAUSE THE DIFFERENCE IS THE THING.
   * Clearing the title restores the registry name; clearing the description REMOVES THE LINE.
   * A control whose two fields clear differently and does not say which surprises somebody
   * either way — which is exactly what §3b asks to be prevented.
   */
  clearRestoresTitle: 'Clear it to go back to the name above.',
  clearHidesHint: 'Clear it to hide the line entirely — the card shows no description at all.',
  titleOverride: 'Title on this dashboard',
  drillOverride: 'Button label',
  width: 'Width',
  row: 'Row',
  destination: 'Goes to',
  destinationPlaceholder: 'Choose a screen',
  widthNote: 'Relative. The grid fits three tiles across on a 1366 laptop and four on a 1920 screen.',
  library: 'Tiles not on this dashboard',
  libraryEmpty: 'Every tile this dashboard can use is already on it.',
  newTile: 'New',
  add: 'Add',
  unavailable: 'Not available on this dashboard',
  unavailableHint: 'These tiles exist and this combination cannot use them, so they are not offered above.',
  /**
   * ⚠️ THE FLOOR IS BUILT FROM `MINIMUM_TILE_IDS`, NEVER TYPED OUT. If the floor's membership
   * changes, this sentence changes with it — `willCreateSummary`'s one-term-list rule.
   */
  emptyRefusal: (): string =>
    `A dashboard cannot be empty. Removing every tile does not hide it — it falls back to ${MINIMUM_TILE_IDS.map(
      (id) => DASHBOARD_TILE_LABEL[id].title,
    ).join(', ')}, which is the floor this system will not go below. Add at least one tile, or reset to the default.`,
  save: 'Save layout',
  saved: 'Layout saved',
  reset: 'Reset to default',
  resetTitle: 'Reset this dashboard to its default?',
  resetBody: 'These tiles will be restored, in this order:',
  resetOverrides: 'Any titles and button labels set here will be discarded.',
  resetConfirm: 'Reset',
  unsaved: 'You have unsaved changes to this dashboard. Leave without saving?',
  previewHeading: 'Preview',
  previewBanner:
    'A preview of the arrangement only. It shows each tile’s outline in its place at its width — it reads no figures, and it does not open anybody’s dashboard.',
  previewDrillNote: 'The line at the foot of a tile is the button an operator will see. It opens nothing here.',
} as const;
